// « Buscar o ir a… ⌘K » (dirección slick, 27/09/2026): una lista de pantallas filtrada al escribir,
// flechas para elegir, Intro para ir. Combobox ARIA: el foco se queda en el campo.
import { useNavigate } from "@tanstack/react-router";
import { MagnifyingGlassIcon } from "@phosphor-icons/react";
import { useEffect, useId, useState, type ComponentType } from "react";
import { Dialog, DialogContent, DialogTitle } from "@responia/ui/components/dialog";
import { cn } from "@responia/ui/lib/utils";
import { useT } from "@/useT";

export type Destino = { etiqueta: string; ruta: string; Icono: ComponentType<{ className?: string }> };

// « Análisis » y « analisis » deben coincidir: sin tildes ni mayúsculas.
// La tecla se dice como en el teclado de quien mira: ⌘K en Mac, Ctrl K en lo demás.
const MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.userAgent);

const plano = (s: string) => s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();

export function Buscador({ destinos }: { destinos: Destino[] }) {
  const t = useT();
  const navegar = useNavigate();
  const [abierto, setAbierto] = useState(false);
  const [texto, setTexto] = useState("");
  const [activo, setActivo] = useState(0);
  const id = useId();
  const lista = destinos.filter((d) => plano(d.etiqueta).includes(plano(texto.trim())));

  useEffect(() => {
    const tecla = (e: KeyboardEvent) => {
      // `key` puede faltar (eventos de autocompletado de Chrome).
      if (e.key?.toLowerCase() === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setAbierto((a) => !a);
      }
    };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, []);

  const abrir = (v: boolean) => {
    setAbierto(v);
    setTexto("");
    setActivo(0);
  };
  const ir = (d?: Destino) => {
    if (!d) return;
    abrir(false);
    navegar({ to: d.ruta });
  };

  return (
    <>
      <button
        type="button"
        data-buscador
        onClick={() => abrir(true)}
        className="flex w-full items-center gap-2 h-8 rounded-lg border bg-card px-2.5 text-[13px] text-muted-foreground shadow-xs transition-colors outline-ring hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2"
      >
        <MagnifyingGlassIcon className="size-3.5 shrink-0" aria-hidden />
        <span className="flex-1 text-start">{t("buscar.abrir")}</span>
        <kbd className="rounded-sm bg-muted px-1.5 font-sans text-[11px] text-muted-foreground">{MAC ? "⌘K" : "Ctrl K"}</kbd>
      </button>
      <Dialog open={abierto} onOpenChange={abrir}>
        <DialogContent showCloseButton={false} className="top-[20%] translate-y-0 gap-0 overflow-hidden p-0 sm:max-w-md" aria-describedby={undefined}>
          <DialogTitle className="sr-only">{t("buscar.titulo")}</DialogTitle>
          <div className="flex items-center gap-2 border-b border-border px-3">
            <MagnifyingGlassIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            <input
              autoFocus
              role="combobox"
              aria-expanded
              aria-autocomplete="list"
              aria-controls={`${id}-lista`}
              aria-activedescendant={lista[activo] ? `${id}-${activo}` : undefined}
              aria-label={t("buscar.titulo")}
              placeholder={t("buscar.abrir")}
              value={texto}
              onChange={(e) => { setTexto(e.target.value); setActivo(0); }}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") { e.preventDefault(); setActivo((a) => Math.max(0, Math.min(a + 1, lista.length - 1))); }
                if (e.key === "ArrowUp") { e.preventDefault(); setActivo((a) => Math.max(a - 1, 0)); }
                if (e.key === "Enter") { e.preventDefault(); ir(lista[activo]); }
              }}
              className="h-11 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
            />
          </div>
          <ul id={`${id}-lista`} role="listbox" aria-label={t("buscar.titulo")} className="max-h-80 overflow-y-auto p-1.5">
            {lista.length === 0 && <li role="presentation" className="px-2.5 py-6 text-center text-sm text-muted-foreground">{t("buscar.vacio")}</li>}
            {lista.map((d, i) => (
              <li
                key={d.ruta}
                id={`${id}-${i}`}
                role="option"
                aria-selected={i === activo}
                onMouseEnter={() => setActivo(i)}
                onClick={() => ir(d)}
                className={cn("flex cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 text-sm", i === activo ? "bg-muted text-foreground" : "text-muted-foreground")}
              >
                <d.Icono className={cn("size-4 shrink-0", i === activo ? "text-primary" : "text-muted-foreground")} />
                {d.etiqueta}
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>
    </>
  );
}
